import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import Loading from '@/components/Loading/Loading';
import Button from '@/components/UI/Button/Button';
import { type Study } from '@/model/Study.model';
import { type GroupApiId, type TreatmentGroup } from '@/model/TreatmentGroup.model';
import { _isNil, _notNil } from '@/littledash';
import { type PlannedAnimalTarget, plannedSampleExportType } from '@/model/SamplePlan.model';
import { SampleNamesTable } from '@/components/Samples/SampleNamesTable/SampleNamesTable';
import { type AnimalV1, type AnimalApiId } from '@/model/Animal.model';
import { type TeamGlossary } from '@/model/Glosary.model';
import GroupingControls from '@/components/Samples/SampleNamesTable/GroupingControls';
import {
  type AnimalMap,
  type GroupMap,
  type SampleDefinitionMap,
  type SampleTypeMap,
  type SamplePlan,
  type ReservedSampleAggregation,
  type GroupDimension,
  type MetaField,
  type MetadataGlossaryV1,
  DEFAULT_GROUPINGS,
} from '@/components/Samples/SampleNamesTable/SampleNames.model';
import ReservedSamplesFlatTable from '@/components/Samples/SampleNamesTable/ReservedSamplesFlatTable';
import { dimensionKey, buildAggQuery } from '@/components/Samples/SampleNamesTable/SampleNames.utils';
import { downloadSamplingPlan } from '@/components/Samples/Samples.utils';
import ApiErrorBanner, { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import { useModalAction } from '@/utils/modal';
import { errorToast } from '@/helpers';
import { isWriteUserForStudy } from '@/support/auth';
import { useAsyncExport } from '@/support/Hooks/api/useAsyncExport';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';

const samplePlanFeatureSelector = createSelector(featuresSelector, (features) => ({
  asyncExportsEnabled: features.async_exports === true,
}));

export const SamplePlanTableViewV2: FC = () => {
  const { id: studyId } = useParams();
  const { openModal } = useModalAction();
  const { asyncExportsEnabled } = useSelector(samplePlanFeatureSelector);
  const triggerAsyncExport = useAsyncExport();
  const [groupings, setGroupings] = useState<Array<GroupDimension>>(DEFAULT_GROUPINGS);

  const { response: studyResponse, loading: studyLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    options: { invokeOnInit: true, onError: { slug: 'sample-names-study-load', toast: false } },
    query: { include: ['users'] },
  });

  const { response: groupsResponse, loading: groupsLoading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: studyId },
    query: { perPage: -1 },
    options: { invokeOnInit: true, onError: { slug: 'sample-names-groups-load', toast: false } },
  });

  const { response: glossaryResponse, loading: glossaryLoading } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    query: { group: 'samples', perPage: -1 },
    options: { onError: { slug: 'sample-names-glossary-load', toast: false } },
  });

  const { response: metaGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: { filter_type: 'sample_meta', perPage: -1 },
    options: { onError: { slug: 'sample-names-meta-glossary-load', toast: false } },
  });

  const {
    response: definitionsResponse,
    loading: definitionsLoading,
    invoke: loadDefinitions,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/reserved-samples/aggregations',
    options: { invokeOnInit: false, onError: { slug: 'sample-names-definitions-load', toast: false } },
  });

  const study = useMemo(
    () => (studyResponse?.type === 'success' ? (studyResponse.body as { data: Study }).data : undefined),
    [studyResponse]
  );
  const studyApiId = study?.api_id;

  const { response: samplePlanResponse, invoke: loadSamplePlan } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples',
    options: { invokeOnInit: false, onError: { slug: 'sample-names-sample-plan-load', toast: false } },
  });

  const { invoke: createSamplingPlanExport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/export/{type}',
    options: { invokeOnInit: false, onError: { slug: 'sample-names-export', toast: false } },
  });

  const { response: registeredAnimalsResponse, invoke: loadRegisteredAnimals } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/animals',
    path: { studyId: 'sdy_invalid' },
    query: { perPage: -1, include: ['study_group'] },
    options: { invokeOnInit: false, onError: { slug: 'sample-names-registered-animals-load', toast: false } },
  });

  const { response: plannedAnimalsResponse, invoke: loadPlannedAnimals } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/animals',
    query: { perPage: -1 },
    options: { invokeOnInit: false, onError: { slug: 'sample-names-planned-animals-load', toast: false } },
  });

  const primaryGrouping = groupings[0];

  useEffect(() => {
    if (_isNil(studyApiId) || _isNil(primaryGrouping)) {
      return;
    }
    loadDefinitions({
      path: { studyApiId },
      query: buildAggQuery(primaryGrouping, []),
    });
  }, [studyApiId, primaryGrouping]);

  useEffect(() => {
    if (_isNil(studyApiId)) {
      return;
    }
    loadSamplePlan({ path: { studyApiId } });
    loadRegisteredAnimals({ path: { studyId: studyApiId }, query: { perPage: -1, include: ['study_group'] } });
    loadPlannedAnimals({ path: { studyApiId }, query: { perPage: -1 } });
  }, [studyApiId]);

  const metaFields = useMemo<Array<MetaField>>(() => {
    if (metaGlossaryResponse?.type !== 'success') {
      return [];
    }
    const glossaries = metaGlossaryResponse.body as Array<MetadataGlossaryV1>;
    return glossaries.reduce<Array<MetaField>>((acc, g) => {
      if (g.active && _notNil(g.api_id) && g.field_type !== 'multi_select') {
        acc.push({ apiId: g.api_id, title: g.title });
      }
      return acc;
    }, []);
  }, [metaGlossaryResponse]);

  const sampleTypeMap = useMemo<SampleTypeMap>(() => {
    const glossaries =
      glossaryResponse?.type === 'success' ? (glossaryResponse.body as { data: Array<TeamGlossary> }).data : [];
    return new Map(
      glossaries.reduce<Array<[string, string]>>((acc, g) => {
        if (_notNil(g.title)) {
          acc.push([g.api_id, g.title]);
        }
        return acc;
      }, [])
    );
  }, [glossaryResponse]);

  const groupMap = useMemo<GroupMap>(() => {
    if (groupsResponse?.type !== 'success') {
      return new Map();
    }
    const groups = (groupsResponse.body as { data: Array<TreatmentGroup> }).data;
    return new Map(
      groups.reduce<Array<[GroupApiId, TreatmentGroup]>>((acc, group) => {
        if (_notNil(group.api_id)) {
          acc.push([group.api_id, group]);
        }
        return acc;
      }, [])
    );
  }, [groupsResponse]);

  const animalMap = useMemo<AnimalMap>(() => {
    const map: AnimalMap = new Map();
    if (plannedAnimalsResponse?.type === 'success') {
      (plannedAnimalsResponse.body as { data: Array<PlannedAnimalTarget> }).data.forEach((animal) => {
        map.set(animal.api_id, { type: 'planned', ...animal });
      });
    }
    if (registeredAnimalsResponse?.type === 'success') {
      (registeredAnimalsResponse.body as { data: Array<AnimalV1> }).data.forEach((animal) => {
        if (_notNil(animal.api_id)) {
          map.set(animal.api_id as AnimalApiId, {
            type: 'registered',
            api_id: animal.api_id as AnimalApiId,
            id: animal.id,
            name: animal.name,
            study_group_api_id: (animal.study_group_api_id ?? null) as GroupApiId | null,
          });
        }
      });
    }
    return map;
  }, [plannedAnimalsResponse, registeredAnimalsResponse]);

  const sampleDefinitionMap = useMemo<SampleDefinitionMap>(() => {
    if (samplePlanResponse?.type !== 'success') {
      return new Map();
    }
    const plan = samplePlanResponse.body as SamplePlan;
    return plan.definitions.reduce<SampleDefinitionMap>((acc, def) => {
      acc.set(def.api_id, def);
      return acc;
    }, new Map());
  }, [samplePlanResponse]);

  const handleRemoveGrouping = useCallback((dimension: GroupDimension) => {
    const key = dimensionKey(dimension);
    setGroupings((prev) => prev.filter((d) => dimensionKey(d) !== key));
  }, []);

  const handleAddGrouping = useCallback((dimension: GroupDimension) => {
    setGroupings((prev) => [...prev, dimension]);
  }, []);

  const handleEdit = useCallback(() => {
    if (_isNil(study)) {
      return;
    }
    if (samplePlanResponse?.type !== 'success') {
      errorToast('Sample plan is still loading. Please wait and try again.');
      return;
    }
    openModal('ADD_SAMPLE_PLANS', {
      studyId: study.id,
      studyApiId: study.api_id,
      onSampleDefinitionsUpdate: () => loadSamplePlan({ path: { studyApiId: study.api_id } }),
      sampleDefinitions: samplePlanResponse.body.definitions,
    });
  }, [study, samplePlanResponse, openModal, loadSamplePlan]);

  const handleExport = useCallback(async () => {
    if (_isNil(study)) {
      return;
    }
    if (asyncExportsEnabled) {
      await triggerAsyncExport(() =>
        createSamplingPlanExport({ path: { studyApiId: study.api_id, type: plannedSampleExportType.sample_plan } })
      );
      return;
    }
    await downloadSamplingPlan(study.api_id);
  }, [study, asyncExportsEnabled, triggerAsyncExport, createSamplingPlanExport]);

  if (studyLoading || groupsLoading) {
    return <Loading />;
  }

  if (studyResponse?.type !== 'success') {
    return (
      <div className="pa4">
        <ApiErrorBanner errorType={ApiErrorType.FETCH} />
      </div>
    );
  }

  if (_isNil(study)) {
    return (
      <div className="pa4">
        <ApiErrorBanner errorType={ApiErrorType.FETCH} />
      </div>
    );
  }

  const definitions: Array<ReservedSampleAggregation> =
    definitionsResponse?.type === 'success'
      ? (definitionsResponse.body as { data: Array<ReservedSampleAggregation> }).data
      : [];

  const showFlatTable = groupings.length === 0;
  const isEmpty = !showFlatTable && !definitionsLoading && _notNil(definitionsResponse) && definitions.length === 0;
  const definitionsError = definitionsResponse?.type === 'error';
  const groupsError = groupsResponse?.type === 'error';
  const samplePlanError = samplePlanResponse?.type === 'error';
  const animalsError = registeredAnimalsResponse?.type === 'error' || plannedAnimalsResponse?.type === 'error';

  const renderTable = () => {
    if (showFlatTable) {
      if (glossaryLoading) {
        return <Loading />;
      }
      return (
        <ReservedSamplesFlatTable
          studyApiId={study.api_id}
          groupMap={groupMap}
          sampleTypeMap={sampleTypeMap}
          metaFields={metaFields}
          sampleDefinitionMap={sampleDefinitionMap}
        />
      );
    }
    if (definitionsError) {
      return (
        <div className="ph3">
          <ApiErrorBanner
            errorType={ApiErrorType.FETCH}
            error={definitionsResponse}
            title="Could not load sample names"
          />
        </div>
      );
    }
    if (definitionsLoading) {
      return <Loading />;
    }
    if (isEmpty) {
      return (
        <div className="flex flex-column items-center bg-light-gray ma4 pa4">
          <div className="f4 lh-copy">No Sample Names</div>
          <div className="mv4">Reserved sample names will appear here once generated</div>
        </div>
      );
    }
    return (
      <SampleNamesTable
        key={groupings.map(dimensionKey).join(',')}
        studyApiId={study.api_id}
        studyId={Number(studyId)}
        definitions={definitions}
        groupMap={groupMap}
        groupings={groupings}
        metaFields={metaFields}
        sampleTypeMap={sampleTypeMap}
        animalMap={animalMap}
        sampleDefinitionMap={sampleDefinitionMap}
      />
    );
  };

  const actionButtons = (
    <>
      {isWriteUserForStudy(study) && (
        <Button plain onClick={handleEdit}>
          Edit
        </Button>
      )}
      <Button primary className="ml2" onClick={handleExport}>
        Export
      </Button>
    </>
  );

  return (
    <div data-test-component="SamplePlanTableViewV2" data-test-element="container">
      <div className="ui-card">
        <GroupingControls
          groupings={groupings}
          onRemove={handleRemoveGrouping}
          onAdd={handleAddGrouping}
          metaFields={metaFields}
          actions={actionButtons}
        />
        {groupsError && (
          <div className="ph3">
            <ApiErrorBanner errorType={ApiErrorType.FETCH} error={groupsResponse} title="Could not load study groups" />
          </div>
        )}
        {samplePlanError && (
          <div className="ph3">
            <ApiErrorBanner
              errorType={ApiErrorType.FETCH}
              error={samplePlanResponse}
              title="Could not load sample definitions — some columns may be incomplete"
            />
          </div>
        )}
        {animalsError && (
          <div className="ph3">
            <ApiErrorBanner
              errorType={ApiErrorType.FETCH}
              title="Could not load animal data — the Animals column may be incomplete"
            />
          </div>
        )}
        {renderTable()}
      </div>
    </div>
  );
};
